import React, { useEffect, useState } from "react";
import DetailsEditing from "../details-editing";
import {
  KrookieFormationTypeId,
  setFormDataEditDetails,
} from "../../../../../services/reducers/master-plan-modification";
import {
  CardKrookies,
  FormDataEditDetails,
  KrookieStatus,
} from "../../../../../interfaces/krookie";
import { useAppDispatch } from "../../../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../../../hooks/useAppSelector";
import { formatDateWithoutTime } from "../../../../../utils/formatDate";
import { getNewChangeNumber } from "../../../../../utils/krookie-helpers";
import { ConvertToLines } from "../../../../../utils/master-plan-helper";
import { getPreviousKrookieNum } from "../../../../../services/api";
import ModalCloseMasterPlan from "../../modals/modal-close-master-plan/modal-close-master-plan";

const DetailsDivider = () => {
  const dispatch = useAppDispatch();
  const {
    formDataEditDetails,
    krookiePlots,
    citiesEn,
    screenshots,
    phase,
    landUse,
    selectedPlots,
    convertedKrookieData,
  } = useAppSelector((state) => state.masterPlanModification);
  const { user } = useAppSelector((state) => state.user);
  const [showModalCloseMasterPlan, setShowModalCloseMasterPlan] =
    useState<boolean>(false);

  const {
    cityId,
    phaseId,
    number,
    landUseId,
    objectId: initialObjectId,
  } = selectedPlots[0];

  const isFormDataReady = krookiePlots.every(
    (plot) => formDataEditDetails[plot.objectId],
  );

  useEffect(() => {
    krookiePlots.forEach((plot, index) => {
      const currentFormData = formDataEditDetails[plot.objectId] || null;
      const currentConvertedKrookieData =
        convertedKrookieData.krookies[plot.objectId] || null;

      if (currentFormData) return;

      const { area, objectId } = plot;

      if (currentConvertedKrookieData) {
        const initialFormData = {
          cityEn: {
            displayValue: currentConvertedKrookieData.cityEn.displayValue,
            value: currentConvertedKrookieData.cityEn.value,
          },
          phase: {
            displayValue: currentConvertedKrookieData.phase.displayValue || "",
            value: currentConvertedKrookieData.phase.value,
          },
          landUse: {
            displayValue:
              currentConvertedKrookieData.landUse.displayValue || "",
            value: currentConvertedKrookieData.landUse.value,
          },
          status: {
            displayValue: currentConvertedKrookieData.status.displayValue || "",
            value: currentConvertedKrookieData.status.value,
          },
          locationStatusCode: {
            displayValue:
              currentConvertedKrookieData.locationStatusCode.displayValue || "",
            value: currentConvertedKrookieData.locationStatusCode.value,
          },
          isInvestmentOpportunity:
            currentConvertedKrookieData.isInvestmentOpportunity,
          plotNumber: currentConvertedKrookieData.plotNumber,
          oldPlotNumber: currentConvertedKrookieData.oldPlotNumber,
          area,
          cityAr: currentConvertedKrookieData.cityAr,
          buildArea: currentConvertedKrookieData.buildArea,
          height: currentConvertedKrookieData.height,
          front: currentConvertedKrookieData.front,
          back: currentConvertedKrookieData.back,
          side: currentConvertedKrookieData.side,
          streetNumber: currentConvertedKrookieData.streetNumber,
          previousKrookieCode: currentConvertedKrookieData.previousKrookieCode,
          notes: currentConvertedKrookieData.notes,
        };
        dispatch(
          setFormDataEditDetails({
            id: objectId,
            formData: initialFormData,
          }),
        );
      } else {
        const initialFormData = {
          cityEn: { displayValue: citiesEn.get(cityId) || "", value: cityId },
          phase: { displayValue: phase.get(phaseId) || "", value: phaseId },
          landUse: {
            displayValue: landUse.get(landUseId) || "",
            value: landUseId,
          },
          plotNumber: `${number}-${index + 1}`,
          oldPlotNumber: number,
          area,
          front: "7.5",
          back: "4.5",
          side: "3.5",
        };

        dispatch(
          setFormDataEditDetails({
            id: objectId,
            formData: { ...initialFormData },
          }),
        );

        getPreviousKrookieNum(initialObjectId).then((res) => {
          const previousCode = res ? res.join(", ") : "";

          dispatch(
            setFormDataEditDetails({
              id: objectId,
              formData: { previousKrookieCode: previousCode },
            }),
          );
        });
      }
    });
  }, []);

  useEffect(() => {
    krookiePlots.forEach((plot) => {
      const currentFormData = formDataEditDetails[plot.objectId];
      if (currentFormData && currentFormData.area !== plot.area) {
        dispatch(
          setFormDataEditDetails({
            id: plot.objectId,
            formData: { area: plot.area },
          }),
        );
      }
    });
  }, [
    krookiePlots.map((plot) => plot.area).join(","),
    krookiePlots.map((plot) => plot.objectId).join(","),
  ]);

  const requiredFields: (keyof FormDataEditDetails)[] = [
    "cityEn",
    "phase",
    "status",
    "locationStatusCode",
    "plotNumber",
    "streetNumber",
    "landUse",
    "buildArea",
    "area",
    "height",
    "front",
    "back",
    "side",
  ];

  if (!isFormDataReady || krookiePlots.length === 0) {
    return null;
  }

  const cardKrookiesData: Record<number, CardKrookies> = Object.fromEntries(
    krookiePlots.map((plot) => {
      const currentFormData = formDataEditDetails[plot.objectId];
      const currentScreenshots = screenshots[plot.objectId];
      const currentConvertedKrookieData =
        convertedKrookieData.krookies[plot.objectId] || null;

      const revision = `${formatDateWithoutTime(new Date())}_${getNewChangeNumber(currentConvertedKrookieData?.historyLog ?? [])}`;

      return [
        plot.objectId,
        {
          ...currentFormData,
          serial: currentFormData.plotNumber,
          previousKrookieCode: currentFormData.previousKrookieCode,
          revision,
          wgs: plot.spatialReference,
          drafterName: user?.userName,
          directorName: "",
          signatureDrafter: formatDateWithoutTime(new Date()),
          signatureDirector: "",
          tableData: ConvertToLines(plot.newGeometry) ?? [],
          screenshots: {
            firstScreenshot: {
              scale: currentScreenshots?.[0]?.scale,
              url: currentScreenshots?.[0]?.url,
            },
            secondScreenshot: {
              scale: currentScreenshots?.[1]?.scale,
              url: currentScreenshots?.[1]?.url,
            },
          },
        },
      ];
    }),
  );

  const firstConvetedKrookieData = Object.values(
    convertedKrookieData.krookies,
  )[0];
  const documentStatus =
    firstConvetedKrookieData?.statusId === KrookieStatus.Rejected
      ? KrookieStatus.Rejected
      : KrookieStatus.Project;

  const handleVisibleCloseDocumentModal = () => {
    setShowModalCloseMasterPlan((prevState) => !prevState);
  };

  return (
    <>
      <DetailsEditing
        requiredFields={requiredFields}
        cardKrookieData={cardKrookiesData}
        krookieFormationTypeId={KrookieFormationTypeId.Divide}
        status={documentStatus}
        disabledFields={{
          cityEn: true,
          phase: true,
          oldPlotNumber: true,
          area: true,
        }}
        onToggleCloseMasterPlanModal={handleVisibleCloseDocumentModal}
      />
      {showModalCloseMasterPlan && (
        <ModalCloseMasterPlan onClose={handleVisibleCloseDocumentModal} />
      )}
    </>
  );
};

export default DetailsDivider;
